import React, { useEffect, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import vb1 from '@/assets/gallery/vb-1.jpg.asset.json';
import vb2 from '@/assets/gallery/vb-2.jpg.asset.json';
import vb3 from '@/assets/gallery/vb-3.jpg.asset.json';
import vb4 from '@/assets/gallery/vb-4.jpg.asset.json';
import vb5 from '@/assets/gallery/vb-5.jpg.asset.json';
import vb6 from '@/assets/gallery/vb-6.jpg.asset.json';
import vb7 from '@/assets/gallery/vb-7.jpg.asset.json';
import vb8 from '@/assets/gallery/vb-8.jpg.asset.json';
import vb9 from '@/assets/gallery/vb-9.jpg.asset.json';
import vb10 from '@/assets/gallery/vb-10.jpg.asset.json';

const images = [
  { src: vb1.url, alt: 'Restored motorcycle carburettor after vapour blasting (vapor blasting)' },
  { src: vb2.url, alt: 'Vintage carburettor body and float bowl restored by vapour blasting' },
  { src: vb3.url, alt: 'Aluminium gearbox housing finished with vapour blasting' },
  { src: vb4.url, alt: 'Two-stroke engine cases cleaned via vapour blasting' },
  { src: vb5.url, alt: 'Motorcycle hub and brake drum restored by vapour blasting' },
  { src: vb6.url, alt: 'Motorcycle engine cylinder head and cases after vapour blasting' },
  { src: vb7.url, alt: 'Close-up of vapour-blasted (vapor blasted) aluminium casting detail' },
  { src: vb8.url, alt: 'Mikuni carburettor body restored with vapour blasting' },
  { src: vb9.url, alt: 'Holley four-barrel carburettor restored by vapor blasting (vapour blasting)' },
  { src: vb10.url, alt: 'Detailed cylinder head fins cleaned by vapour blasting' },
];

const GallerySection: React.FC = () => {
  const [index, setIndex] = useState<number | null>(null);

  const close = useCallback(() => setIndex(null), []);
  const prev = useCallback(
    () => setIndex((i) => (i === null ? i : (i - 1 + images.length) % images.length)),
    [],
  );
  const next = useCallback(
    () => setIndex((i) => (i === null ? i : (i + 1) % images.length)),
    [],
  );

  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [index, close, prev, next]);

  return (
    <section id="gallery" className="section-padding bg-background scroll-mt-24">
      <div className="container-wide mx-auto">
        <div className="glass-card rounded-2xl p-6 md:p-10">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <p className="text-primary font-medium tracking-[0.3em] uppercase mb-4 text-sm">
              Our Work
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-bold mb-4 text-foreground">
              Finished examples of our{' '}
              <span className="gradient-text">vapour blasting services</span>
            </h2>
            <p className="text-muted-foreground">
              Real customer parts — carburettors, engine cases, hubs and cylinder heads — restored
              in our Christchurch workshop. Click any image to enlarge.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
            {images.map((img, i) => (
              <button
                key={img.src}
                type="button"
                onClick={() => setIndex(i)}
                className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-black hover:border-primary/60 transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label={`Open image: ${img.alt}`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                  <ZoomIn className="w-7 h-7 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {index !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label="Image gallery"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); close(); }}
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-background/10 hover:bg-primary text-foreground hover:text-primary-foreground flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-background/10 hover:bg-primary text-foreground hover:text-primary-foreground flex items-center justify-center transition-colors"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-background/10 hover:bg-primary text-foreground hover:text-primary-foreground flex items-center justify-center transition-colors"
            aria-label="Next image"
          >
            <ChevronRight className="w-7 h-7" />
          </button>
          <figure
            className="max-w-6xl max-h-[88vh] w-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[index].src}
              alt={images[index].alt}
              className="max-h-[82vh] w-auto max-w-full object-contain rounded-lg shadow-2xl"
            />
            <figcaption className="mt-3 text-sm text-muted-foreground text-center">
              {images[index].alt} — {index + 1} / {images.length}
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  );
};

export default GallerySection;
